import React, { useEffect, useState } from 'react';
import { Inventory, BattleResult } from '../types';
import { calculateCasualties, PIECE_VALUES } from '../utils/warUtils';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ShieldAlert, Skull, Crown, TrendingUp, TrendingDown, Minus, Play, Swords } from 'lucide-react';
import { SupabaseService } from '../services/supabaseService';
import { supabase } from '../services/supabaseClient';
import { EloRank } from './EloRank';

interface BattleAftermathProps {
    warId: string;
    battleId: string;
    result: BattleResult;
    initialPlayerPawns: number;
    initialEnemyPawns: number;
    finalPlayerPawns: number;
    finalEnemyPawns: number;
    userSide: 'white' | 'black';
}

const PIECE_ICONS: Record<keyof Inventory, string> = {
    p: '♟', n: '♞', b: '♝', r: '♜', q: '♛', k: '♚'
};



// ... (Rest of imports/constants kept, jumping to Component)

const GOLD_COSTS: Record<string, number> = {
    p: 5, n: 9, b: 9, r: 12, q: 30, k: 100
};

const WarStockChart: React.FC<{ warId: string; currentResult: BattleResult; userSide: 'white' | 'black' }> = ({ warId, currentResult, userSide }) => {
    const [history, setHistory] = useState<number[]>([0]); // Start at 0 (neutral)
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchHistory = async () => {
            const battles = await SupabaseService.getBattlesByWarId(warId);

            let cumulativeBalance = 0;
            const balanceHistory = [0];

            battles.forEach(b => {
                if (b.status !== 'completed' || !b.result) return;

                // Calculate Net Value Change for HOST (Player 1)
                // Casualties = Host Losses. Enemy_Casualties = Guest Losses.
                let change = 0;

                // Value Gained (Guest died)
                (Object.keys(b.result!.enemy_casualties) as Array<keyof Inventory>).forEach(k => {
                    change += (b.result!.enemy_casualties[k] || 0) * (PIECE_VALUES[k] || 0);
                });

                // Value Lost (Host died)
                (Object.keys(b.result!.casualties) as Array<keyof Inventory>).forEach(k => {
                    change -= (b.result!.casualties[k] || 0) * (PIECE_VALUES[k] || 0);
                });

                cumulativeBalance += change;
                balanceHistory.push(cumulativeBalance);
            });

            // If I am Guest (Black/Opponent), invert the graph so Up = Good for Me
            if (userSide === 'black') { // Assuming Host is White default
                setHistory(balanceHistory.map(v => v * -1));
            } else {
                setHistory(balanceHistory);
            }
            setLoading(false);
        };
        fetchHistory();
    }, [warId, userSide]);

    if (loading) return <div className="h-32 flex items-center justify-center text-xs text-tudor-gold animate-pulse">Consulting Royal Ledgers...</div>;

    // RENDER CHART
    const min = Math.min(...history, -5);
    const max = Math.max(...history, 5);
    const range = max - min || 10;

    // SVG Dimensions
    const width = 300;
    const height = 150;
    const padding = 20;

    // Map data to points
    const points = history.map((val, i) => {
        const x = padding + (i / (history.length - 1 || 1)) * (width - 2 * padding);
        const normalizedY = (val - min) / range; // 0..1
        const y = (height - padding) - (normalizedY * (height - 2 * padding));
        return `${x},${y}`;
    }).join(' ');

    const lastVal = history[history.length - 1];

    // Aesthetic: Tudor Gold only
    const lineColor = "#C5A059";

    return (
        <div className="bg-black/40 border border-tudor-gold/30 p-4 rounded relative overflow-hidden shadow-[0_0_20px_rgba(197,160,89,0.1)]">
            {/* Main Value Display */}
            <div className="absolute top-2 right-4 z-10 text-right">
                <div className={`text-3xl font-mono font-bold text-tudor-gold`}>
                    {lastVal > 0 ? '+' : ''}{lastVal}
                </div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Net War Advantage</div>
            </div>

            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-40 overflow-visible" preserveAspectRatio="none">
                {/* Axes */}
                {/* Y Axis */}
                <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#555" strokeWidth="1" />
                {/* X Axis */}
                <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#555" strokeWidth="1" />

                {/* Zero Line (if visible) */}
                {min < 0 && max > 0 && (
                    <line
                        x1={padding}
                        y1={(height - padding) - ((0 - min) / range * (height - 2 * padding))}
                        x2={width - padding}
                        y2={(height - padding) - ((0 - min) / range * (height - 2 * padding))}
                        stroke="#444"
                        strokeWidth="0.5"
                        strokeDasharray="4"
                    />
                )}

                {/* Trend Line */}
                <polyline
                    fill="none"
                    stroke={lineColor}
                    strokeWidth="2"
                    points={points}
                    className="drop-shadow-[0_0_4px_rgba(197,160,89,0.8)]"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                />

                {/* Area Gradient */}
                <linearGradient id="goldGradient" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={lineColor} stopOpacity="0.2" />
                    <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
                </linearGradient>
                <polygon
                    points={`${padding},${height - padding} ${points} ${width - padding},${height - padding}`}
                    fill="url(#goldGradient)"
                />

                {/* Points */}
                {history.map((val, i) => {
                    const x = padding + (i / (history.length - 1 || 1)) * (width - 2 * padding);
                    const y = (height - padding) - ((val - min) / range * (height - 2 * padding));
                    return (
                        <circle key={i} cx={x} cy={y} r="2" fill={lineColor} />
                    );
                })}

                {/* Axis Labels */}
                <text x={width / 2} y={height} fill="#666" fontSize="10" textAnchor="middle" fontFamily="monospace">BATTLE SEQUENCE</text>
                <text x="5" y={height / 2} fill="#666" fontSize="10" textAnchor="middle" fontFamily="monospace" transform={`rotate(-90, 5, ${height / 2})`}>ATTRITION vs DAMAGE</text>
            </svg>
        </div>
    );
};

const TreasonPanel: React.FC<{
    prisoners: Inventory;
    warId: string;
    result: BattleResult;
    userSide: 'white' | 'black';
    currentGold: number;
}> = ({ prisoners, warId, result, userSide, currentGold }) => {
    const [available, setAvailable] = useState<Inventory>({ ...prisoners });
    const [feedback, setFeedback] = useState<Record<string, string>>({});

    // Track local gold changes to update UI immediately
    const [localGold, setLocalGold] = useState(currentGold);

    const handleAction = async (type: string, action: 'recruit' | 'ransom') => {
        // Base Chance for Recruiting
        let chance = 0.5;
        if (type === 'p') chance = 0.7;
        if (type === 'n' || type === 'b') chance = 0.6;
        if (type === 'r') chance = 0.5;
        if (type === 'q') chance = 0.3;

        // Winner Bonus
        if (result.winner === userSide.charAt(0)) chance += 0.15;

        const { success, msg } = await SupabaseService.processPrisoner(warId, type as keyof Inventory, action, chance);

        setFeedback(prev => ({ ...prev, [type]: msg }));
        // Decrement local count
        setAvailable(prev => ({ ...prev, [type as keyof Inventory]: Math.max(0, prev[type as keyof Inventory] - 1) }));

        // Optimistic Gold Update
        if (action === 'ransom') {
            const val = { p: 2, n: 5, b: 5, r: 15, q: 30, k: 50 }[type as keyof Inventory] || 0;
            setLocalGold(prev => prev + val);
        }
    };

    const hasPrisoners = Object.values(available).some(c => c > 0);
    if (!hasPrisoners) return null;

    const RANSOM_VALUES: Record<string, number> = { p: 2, n: 5, b: 5, r: 15, q: 30, k: 50 };
    const isDebt = localGold < 0;

    return (
        <div className={`mb-12 border ${isDebt ? 'border-red-900/50 bg-red-950/10' : 'border-tudor-gold/30 bg-zinc-900'} p-6 rounded relative overflow-hidden animate-fade-in-up transition-colors duration-500`}>
            {isDebt && (
                <div className="absolute top-0 right-0 bg-red-900/80 text-white text-[10px] font-bold px-3 py-1 uppercase tracking-widest rounded-bl flex items-center gap-2">
                    <ShieldAlert size={12} /> Crown Debt: Ransom Seized
                </div>
            )}

            <h3 className="text-xl font-serif text-tudor-gold uppercase tracking-widest mb-4 flex items-center gap-2">
                <Skull size={24} /> Prisoners of War
            </h3>
            <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">
                    Their fate is in your hands.
                </p>
                {isDebt && <span className="text-xs text-red-400 font-mono">Treasury: {localGold}g</span>}
            </div>

            <div className="flex flex-wrap gap-4 justify-center">
                {Object.entries(available).map(([type, count]) => (
                    Array.from({ length: count }).map((_, i) => (
                        <div key={`${type}-${i}`} className="bg-black border border-zinc-800 p-4 flex flex-col items-center min-w-[140px] relative group">
                            <span className="text-3xl mb-2">{PIECE_ICONS[type as keyof Inventory]}</span>
                            <span className="text-xs font-mono text-zinc-500 mb-2 uppercase">{type}</span>

                            <div className="flex flex-col gap-2 w-full">
                                <button onClick={() => handleAction(type, 'recruit')} className="bg-tudor-gold hover:bg-white text-black text-xs font-bold py-2 uppercase transition-all">
                                    Join or Perish
                                </button>
                                <button
                                    onClick={() => handleAction(type, 'ransom')}
                                    className={`text-[10px] uppercase py-1 border transition-all flex items-center justify-center gap-1 ${isDebt
                                        ? 'bg-red-900/20 hover:bg-red-900/40 text-red-500 border-red-900/50'
                                        : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400 border-zinc-700'
                                        }`}
                                >
                                    {isDebt ? <span>Repay Debt</span> : <span>Ransom</span>}
                                    <span className="font-mono">({isDebt ? '-' : '+'}{RANSOM_VALUES[type]}g)</span>
                                </button>
                            </div>
                        </div>
                    ))
                ))}
            </div>

            <div className="mt-6 flex flex-col items-center gap-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-600">Recent Decisions</div>
                {Object.entries(feedback).map(([k, v], i) => (
                    <div key={i} className={`text-xs uppercase font-bold animate-pulse ${v.includes('JOINED') ? 'text-green-500' : v.includes('SOLD') ? (isDebt ? 'text-red-400' : 'text-tudor-gold') : 'text-red-500'}`}>
                        {k.toUpperCase()}: {v}
                    </div>
                ))}
            </div>
        </div>
    );
};

export const BattleAftermath: React.FC<BattleAftermathProps> = ({
    warId,
    battleId,
    result,
    initialPlayerPawns,
    initialEnemyPawns,
    finalPlayerPawns,
    finalEnemyPawns,
    userSide
}) => {
    const navigate = useNavigate();
    const [showContent, setShowContent] = useState(false);
    const [currentRating, setCurrentRating] = useState<number | null>(null);
    const [profile, setProfile] = useState<any | null>(null);
    const [rematchStatus, setRematchStatus] = useState<'idle' | 'requested' | 'accepted'>('idle');

    // Calculate Strategic Status (Attrition Based)
    const pawnLoss = initialPlayerPawns - finalPlayerPawns;
    // Strategic Loss: Even if you won, if you have very few pawns left (< 5) or lost too many in one go (> 3)
    // Only applies if you actually won the battle (Victory but Pyrrhic)
    const isVictory = result.winner === userSide.charAt(0); // 'w' or 'b'
    const isStrategicLoss = isVictory && (pawnLoss >= 3 || finalPlayerPawns < 5);

    useEffect(() => {
        setTimeout(() => setShowContent(true), 100);
        SupabaseService.getProfile().then(p => {
            setProfile(p);
            setCurrentRating(p.rating);
        });

        // Subscribe to Rematch Updates
        if (warId === 'quickplay') {
            const channel = supabase.channel(`rematch_${battleId}`)
                .on(
                    'postgres_changes',
                    { event: 'UPDATE', schema: 'public', table: 'battles', filter: `id=eq.${battleId}` },
                    (payload: any) => {
                        const newSettings = payload.new.settings;
                        if (newSettings?.rematch_battle_id) {
                            setRematchStatus('accepted');
                            // Navigate after short delay
                            setTimeout(() => {
                                // Quick Play Battles use War ID param as Battle ID technically?
                                // No, for QuickPlay, `Battle.tsx` handles `warId` param as Battle ID.
                                // So we navigate to `/war-room/[battleId]`?
                                // Wait, the router path is `/war-room/:warId`.
                                // Battle.tsx is mounted on `/war-room/:warId`.
                                // So navigating to `/war-room/${newBattleId}` works perfectly for QuickPlay.
                                navigate(`/war-room/${newSettings.rematch_battle_id}`);
                            }, 1000);
                        }
                    }
                )
                .subscribe();

            return () => { supabase.removeChannel(channel); };
        }
    }, [warId, battleId, navigate]);

    const eloChange = result.eloChange || 0;

    let title = "Stalemate";
    if (result.winner === userSide.charAt(0)) title = "Tactical Victory";
    else if (result.winner !== 'draw') title = "Tactical Defeat";

    return (
        <div className={`w-full max-w-2xl mx-auto bg-tudor-black border-4 ${isStrategicLoss ? 'border-tudor-gold' : 'border-tudor-gray'} p-8 shadow-2xl transition-opacity duration-1000 ${showContent ? 'opacity-100' : 'opacity-0'}`}>

            {/* HEADER: THE VERDICT */}
            <div className="text-center mb-8">
                <h2 className="text-5xl font-serif font-bold tracking-widest text-tudor-gold mb-2 uppercase">
                    {title}
                </h2>
                {isStrategicLoss && (
                    <div className="flex items-center justify-center gap-2 text-tudor-gold/80 animate-pulse border border-tudor-gold/30 p-2 mx-auto w-fit bg-tudor-gold/5 rounded">
                        <ShieldAlert size={24} />
                        <span className="text-xl font-bold tracking-wide uppercase">Strategic Loss Warning</span>
                    </div>
                )}
            </div>

            {/* ELO & RANK UPDATE */}
            {currentRating !== null && (
                <div className="flex flex-col items-center mb-10 bg-tudor-gray/10 p-4 border border-tudor-gold/20 rounded">
                    <div className="text-xs uppercase tracking-widest text-gray-400 mb-2">Commander Rating</div>
                    <div className="flex items-center gap-6">
                        <EloRank rating={currentRating} size="lg" />

                        {eloChange !== 0 && (
                            <div className={`flex items-center gap-1 font-mono text-xl ${eloChange > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {eloChange > 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                                <span>{eloChange > 0 ? '+' : ''}{eloChange}</span>
                            </div>
                        )}
                        {eloChange === 0 && <span className="text-gray-500 italic flex items-center gap-1"><Minus size={16} /> No Change</span>}
                    </div>
                </div>
            )}

            {/* TREASON GAMBLE PANEL */}
            <TreasonPanel
                prisoners={result.enemy_casualties}
                warId={warId}
                result={result}
                userSide={userSide}
                currentGold={currentRating !== null ? (profile?.gold ?? 0) : 0}
            />

            {/* THE LEDGER */}
            <div className="grid grid-cols-2 gap-8 mb-12 relative">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-tudor-gray/30 -translate-x-1/2"></div>

                {/* Friendly Casualties */}
                <div className="text-right pr-4">
                    <h3 className="text-tudor-text/50 font-bold uppercase mb-4 border-b border-tudor-gray/30 pb-2">User Casualties</h3>
                    <div className="space-y-2">
                        {Object.entries(result.casualties).map(([type, count]) => (
                            count > 0 && (
                                <div key={type} className="flex items-center justify-end gap-3 text-red-500 font-mono text-lg animate-fade-in-up">
                                    <span>-{count}</span>
                                    <span className="text-2xl filter drop-shadow-[0_0_3px_rgba(255,0,0,0.5)]">{PIECE_ICONS[type as keyof Inventory]}</span>
                                </div>
                            )
                        ))}
                        {Object.values(result.casualties).every(c => c === 0) && <span className="text-green-500/50 italic">None reported</span>}
                    </div>
                </div>

                {/* Opponent Losses */}
                <div className="pl-4 opacity-50 grayscale">
                    <h3 className="text-tudor-text/50 font-bold uppercase mb-4 border-b border-tudor-gray/30 pb-2">Opponent Losses</h3>
                    <div className="space-y-2">
                        {Object.entries(result.enemy_casualties).map(([type, count]) => (
                            count > 0 && (
                                <div key={type} className="flex items-center justify-start gap-3 text-gray-400 font-mono text-lg animate-fade-in-up">
                                    <span className="text-2xl">{PIECE_ICONS[type as keyof Inventory]}</span>
                                    <span>-{count}</span>
                                </div>
                            )
                        ))}
                        {Object.values(result.enemy_casualties).every(c => c === 0) && <span className="text-xs italic text-gray-600">No confirmed kills</span>}
                    </div>
                </div>
            </div>

            {/* WAR TRAJECTORY CHART ("The Stock Market") */}
            <div className="mb-12">
                <div className="text-sm uppercase tracking-widest text-tudor-gold/60 mb-4 flex justify-between items-center">
                    <span>War Effort Trajectory</span>
                    <span className="text-[10px] bg-tudor-gold/10 text-tudor-gold px-2 py-1 rounded">TUDOR EXCHANGE</span>
                </div>

                <WarStockChart
                    warId={warId}
                    currentResult={result}
                    userSide={userSide}
                />
            </div>

            {/* FOOTER ACTIONS */}
            <div className="flex gap-4">
                {warId === 'quickplay' ? (
                    <>
                        <button
                            onClick={() => {
                                if (rematchStatus === 'idle') {
                                    setRematchStatus('requested');
                                    SupabaseService.requestRematch(battleId, userSide);
                                }
                            }}
                            disabled={rematchStatus !== 'idle'}
                            className={`flex-1 font-bold py-4 px-6 transition-colors flex items-center justify-center gap-2 group border ${rematchStatus === 'idle'
                                ? 'bg-tudor-gold text-tudor-black hover:bg-yellow-600 border-tudor-gold animate-pulse'
                                : 'bg-transparent text-tudor-gold border-tudor-gold/50 cursor-not-allowed opacity-80'
                                }`}
                        >
                            <Swords size={20} className={rematchStatus === 'requested' ? 'animate-spin-slow' : ''} />
                            <span>
                                {rematchStatus === 'idle' && "REQUEST REMATCH"}
                                {rematchStatus === 'requested' && "AWAITING OPPONENT..."}
                                {rematchStatus === 'accepted' && "STARTING DUEL..."}
                            </span>
                        </button>

                        {rematchStatus === 'idle' && (
                            <button
                                onClick={() => navigate('/quickplay')}
                                className="bg-zinc-800 border border-zinc-700 text-zinc-300 font-bold py-4 px-6 hover:bg-zinc-700 hover:text-white transition-colors"
                            >
                                FIND NEW OPPONENT
                            </button>
                        )}

                        <button
                            onClick={() => navigate('/court')}
                            className="bg-transparent border border-tudor-gray text-gray-400 font-bold py-4 px-6 hover:bg-tudor-gray/20 transition-colors"
                        >
                            RETURN TO COURT
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            onClick={() => navigate(`/replay/${battleId}`)}
                            className="flex-1 bg-zinc-800 border border-zinc-700 text-zinc-300 font-bold py-4 px-6 hover:bg-tudor-gold hover:text-black hover:border-tudor-gold transition-colors flex items-center justify-center gap-2 group"
                        >
                            <Play size={18} />
                            <span>REVIEW BATTLE</span>
                        </button>

                        <button
                            onClick={() => navigate('/campaigns')}
                            className="flex-1 bg-tudor-gold text-tudor-black font-bold py-4 px-6 hover:bg-yellow-600 transition-colors flex items-center justify-center gap-2 group"
                        >
                            <Crown size={20} />
                            <span>ROYAL ARCHIVES</span>
                            <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
                        </button>

                        <button
                            onClick={() => navigate('/')}
                            className="bg-transparent border border-tudor-gray text-gray-400 font-bold py-4 px-6 hover:bg-tudor-gray/20 transition-colors"
                        >
                            WITHDRAW
                        </button>
                    </>
                )}
            </div>

        </div>
    );
};
